export { resolveLegalStatus } from '@/lib/company-legal-status';
import type { TFunction } from 'i18next';
import type {
  CompanyBasicResponse,
  CompanyFinancialsResponse,
  ValidationLogDetails,
} from '@/lib/backend-api';
import { companyCountryCode } from '@/lib/company-utils';
import { delocalizePath } from '@/lib/i18n-routes';
import type { CompanyViewInput } from '@/lib/recent-activity-types';
import { DOWNLOADABLE_FILE_TYPES } from './companyDetailUtils';
import type { BalanceSheetFlatRow } from '@/new_components/balance_sheet/balanceSheetService';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import {
  extractCostsForYear,
  getGkvCategories,
  getUkvCategories,
  isUkvStyle,
} from './costCategories';

// Pure derivations behind CompanyDetailPage's state, memos and effects (the
// hooks and their dependency arrays stay in the component).

export function buildAktivaRows(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): BalanceSheetFlatRow[] | null {
  return (
    effectiveFinancialsData?.aktiva_data?.map((row) => ({
      year: row.year,
      section: row.section ?? null,
      subsection: row.subsection ?? null,
      subsubsection: row.subsubsection ?? null,
      title: row.title,
      current_value: row.current_value ?? null,
      previous_value: row.previous_value ?? null,
    })) ?? null
  );
}

export function buildPassivaRows(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): BalanceSheetFlatRow[] | null {
  return (
    effectiveFinancialsData?.passiva_data?.map((row) => ({
      year: row.year,
      section: row.section ?? null,
      subsection: row.subsection ?? null,
      subsubsection: row.subsubsection ?? null,
      title: row.title,
      current_value: row.current_value ?? null,
      previous_value: row.previous_value ?? null,
    })) ?? null
  );
}

export function buildGuvRows(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): GuvFlatRow[] | null {
  return (
    effectiveFinancialsData?.guv_data?.map((row) => ({
      year: row.year,
      section: row.section ?? null,
      subsection: row.subsection ?? null,
      subsubsection: row.subsubsection ?? null,
      subsubsubsection: row.subsubsubsection ?? null,
      title: row.title,
      current_value: row.current_value ?? null,
      previous_value: row.previous_value ?? null,
    })) ?? null
  );
}

export function buildAiGuvRows(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): GuvFlatRow[] | null {
  return (
    effectiveFinancialsData?.ai_guv_data?.map((row) => ({
      year: row.year,
      section: row.section ?? null,
      subsection: row.subsection ?? null,
      subsubsection: row.subsubsection ?? null,
      subsubsubsection: row.subsubsubsection ?? null,
      title: row.title,
      current_value: row.current_value ?? null,
      previous_value: row.previous_value ?? null,
    })) ?? null
  );
}

export function computeHasCostBreakdown(
  mergedGuvData: GuvFlatRow[],
  t: TFunction,
): boolean {
  if (mergedGuvData.length === 0) return false;
  const categories = isUkvStyle(mergedGuvData)
    ? getUkvCategories(t)
    : getGkvCategories(t);
  const years = new Set(mergedGuvData.map((d) => d.year));
  for (const year of years) {
    const yearData = mergedGuvData.filter((d) => d.year === year);
    const sum = extractCostsForYear(yearData, categories).reduce(
      (s, c) => s + c.value,
      0,
    );
    if (sum > 0) return true;
  }
  return false;
}

export function collectAvailableFinancialYears(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): number[] {
  const years = new Set<number>();
  effectiveFinancialsData?.metrics?.forEach((m) => {
    if (typeof m.year === 'number') years.add(m.year);
  });
  const collectYears = (rows: { year?: number }[] | undefined | null) => {
    rows?.forEach((r) => {
      if (typeof r.year === 'number') years.add(r.year);
    });
  };
  collectYears(effectiveFinancialsData?.aktiva_data);
  collectYears(effectiveFinancialsData?.passiva_data);
  collectYears(effectiveFinancialsData?.guv_data);
  collectYears(effectiveFinancialsData?.ai_guv_data);
  return Array.from(years);
}

export function buildRevenueHistory(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
) {
  const employeeByYear = new Map(
    effectiveFinancialsData?.employee_data?.map((e) => [
      e.year,
      e.total ?? null,
    ]) ?? [],
  );
  return (
    effectiveFinancialsData?.metrics?.map((m) => ({
      year: m.year,
      totalRevenue: m.total_revenue ?? null,
      profit: m.profit ?? null,
      totalEmployees: employeeByYear.get(m.year) ?? null,
      totalBalanceSum: m.total_balance_sum ?? m.passiva ?? null,
      grossProfit: m.gross_profit ?? null,
      otherOperatingIncome: m.other_operating_income ?? null,
      sizeClass: m.size_class ?? null,
      revenueCeiling: m.revenue_ceiling ?? null,
      sizeBasisCode: m.size_basis_code ?? null,
      ceilingBasisCode: m.ceiling_basis_code ?? null,
    })) ?? []
  );
}

export function buildValidationErrors(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
) {
  const logs = effectiveFinancialsData?.validation_logs ?? [];
  const yearGroups = new Map<
    number,
    {
      errorType: string;
      severity: string;
      message: string;
      details: ValidationLogDetails | null | undefined;
    }[]
  >();

  for (const log of logs) {
    const year = log.year ?? 0;
    if (!yearGroups.has(year)) yearGroups.set(year, []);

    let message = 'Validation error';
    if (log.details && typeof log.details === 'object') {
      const parts: string[] = [];
      if (log.details.ratio !== undefined)
        parts.push(`ratio: ${log.details.ratio}`);
      if (log.details.current_val !== undefined)
        parts.push(`current: ${log.details.current_val}`);
      if (log.details.previous_val !== undefined)
        parts.push(`previous: ${log.details.previous_val}`);
      if (parts.length > 0) message += ` (${parts.join(', ')})`;
    }

    yearGroups.get(year)!.push({
      errorType: log.error_type ?? 'Unknown',
      severity: log.severity ?? 'ERROR',
      message,
      details: log.details,
    });
  }

  const entries = Array.from(yearGroups.entries())
    .sort(([a], [b]) => b - a)
    .map(([year, errors]) => ({
      year: year || null,
      errorCount: errors.length,
      errors,
    }));

  return {
    total: entries.reduce((sum, e) => sum + e.errorCount, 0),
    entries,
  };
}

/** The recent-views entry for a company page visit (browser only: reads the path). */
export function buildCompanyViewInput(
  basicData: CompanyBasicResponse,
): CompanyViewInput {
  const legalForm: unknown = [
    ...(basicData.company_history?.rechtsform ?? []),
  ].sort((a, b) =>
    (b.entry_date ?? '').localeCompare(a.entry_date ?? ''),
  )[0]?.data.rechtsform;
  return {
    euId: basicData.eu_id,
    companyName: basicData.display_name || basicData.eu_id,
    subtitle:
      [
        basicData.city || basicData.sitz,
        typeof legalForm === 'string' ? legalForm : null,
      ].filter(Boolean).join(' · ') || null,
    countryCode: companyCountryCode(basicData.register_type, basicData.eu_id),
    path: delocalizePath(window.location.pathname).pathname,
  };
}

export function hasDownloadableFinancialDocs(
  effectiveFinancialsData: CompanyFinancialsResponse | null,
): boolean {
  return (effectiveFinancialsData?.financial_files ?? []).some((f) =>
    DOWNLOADABLE_FILE_TYPES.includes(f.file_type),
  );
}
